import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, ignoreElements, map, Observable, of, startWith, tap } from 'rxjs'
import { WatchListResponseInterface } from '@modules/watch-list-module/interfaces/watch-list-response.interface'
import { WatchListService } from '@modules/watch-list-module/services/watch-list.service'
import { WatchListHelperService } from '@modules/watch-list-module/services/watch-list-helper.service'
import { OrderStateEnum } from '@modules/orders-module/enums/order-state.enum'
import { OptionsOrdersService } from '@modules/options-module/orders/services/options-orders.service'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { UserService } from '@modules/user-module/services/user.service'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { PositionsOptionsService } from '@modules/options-module/positions/services/positions-options.service'
import { PositionsOptionsHelperService } from '@modules/options-module/positions/services/positions-options-helper.service'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { ProcessingUserService } from '@modules/user-module/services/processing-user.service'
import { TradingViewHelperService } from '@modules/trading-view-module/services/trading-view-helper.service'
import { TradingViewTimeframesEnum } from '@modules/trading-view-module/enums/trading-view-timeframes.enum'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { StocksOrdersService } from '@modules/stocks-module/orders/services/stocks-orders.service'
import { PositionsStocksService } from '@modules/stocks-module/positions/services/positions-stocks.service'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { PositionsStocksHelperService } from '@modules/stocks-module/positions/services/positions-stocks-helper.service'
import { PostTradingHelperService } from '@modules/options-module/post-trading/services/post-trading-helper.service'
import { PostTradingEventTypeEnum } from '@modules/options-module/post-trading/enums/post-trading-event-type.enum'
import { PostTradingEventInterface } from '@modules/options-module/post-trading/interfaces/post-trading-event.interface'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'

@Injectable({
	providedIn: 'root',
})
export class TradingLauncherService {
	protected readonly object: ObjectConstructor = Object
	protected readonly orderStateEnum: typeof OrderStateEnum = OrderStateEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum
	protected readonly tradingViewTimeframesEnum: typeof TradingViewTimeframesEnum = TradingViewTimeframesEnum
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum

	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _userService: UserService = inject<UserService>(UserService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _ordersStocksService: StocksOrdersService = inject<StocksOrdersService>(StocksOrdersService)
	private readonly _watchListService: WatchListService = inject<WatchListService>(WatchListService)
	private readonly _positionsStocksService: PositionsStocksService =
		inject<PositionsStocksService>(PositionsStocksService)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _optionsOrderService: OptionsOrdersService = inject<OptionsOrdersService>(OptionsOrdersService)
	private readonly _positionsStocksHelperService: PositionsStocksHelperService =
		inject<PositionsStocksHelperService>(PositionsStocksHelperService)
	private readonly _watchListHelperService: WatchListHelperService =
		inject<WatchListHelperService>(WatchListHelperService)
	private readonly _positionsOptionsService: PositionsOptionsService =
		inject<PositionsOptionsService>(PositionsOptionsService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)
	private readonly _positionOptionsHelperService: PositionsOptionsHelperService =
		inject<PositionsOptionsHelperService>(PositionsOptionsHelperService)
	private readonly _processingUserService: ProcessingUserService =
		inject<ProcessingUserService>(ProcessingUserService)
	private readonly _tradingViewHelperService: TradingViewHelperService =
		inject<TradingViewHelperService>(TradingViewHelperService)
	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)
	private readonly _postTradingHelperService: PostTradingHelperService =
		inject<PostTradingHelperService>(PostTradingHelperService)

	public dropStates(): void {
		// processing user
		this._processingUserService.isSuccessProcessedUser = false

		// alpaca account
		this._alpacaAccountHelperService.updateAlpacaAccount({}, true)

		// user detail
		this._userHelperService.toggleWorkingUserSocket(false)
		this._userHelperService.userDetail$.next(null)
		this._userHelperService.isLoadingUserDetail$.next(false)

		// positions stocks
		this._positionsStocksHelperService.positions$.next([])
		this._positionsStocksHelperService.isLoadingPositions$.next(false)

		// watch list
		this._watchListHelperService.watchList$.next(null)
		this._watchListHelperService.isLoadingWatchList$.next(false)

		for (const state of this.object.values(this.orderStateEnum)) {
			this._ordersStocksHelperService.ordersResponseMap.get(state)?.next(null)
			this._ordersStocksHelperService.ordersLoadingMap.get(state)?.next(false)
		}

		// orders options
		for (const state of this.object.values(this.optionOrderStateEnum)) {
			this._optionsOrderHelperService.optionsOrdersResponseMap.get(state)?.next(null)
			this._optionsOrderHelperService.optionsOrdersLoadingMap.get(state)?.next(false)
			this._optionsOrderHelperService.isNeedUpdateOptionsOrderResponseMap.get(state)?.next(false)
		}
		// positions options
		this._positionOptionsHelperService.positionsOptions$.next([])
		this._positionOptionsHelperService.isLoadingPositionsOptions$.next(false)

		// dashboards / widgets
		this._widgetsService.setDashboards([])

		// live ticker
		// Сам liveTicker не сбрасываем, из-за метода _checkUseLiveTicker() в dashboard.component.ts

		// trading view
		this._tradingViewHelperService.setTradingViewState(null)
		this._tradingViewHelperService.setTradingViewTimeframe(this.tradingViewTimeframesEnum.oneDay)
	}

	public launchUserDetail$(): Observable<[UserDetailInterface | null, boolean]> {
		const isNeedRequest: boolean =
			!this._userHelperService.userDetail$.getValue() && !this._userHelperService.isLoadingUserDetail$.getValue()

		return combineLatest([
			this._userHelperService.userDetail$,
			this._userHelperService.isLoadingUserDetail$,
			isNeedRequest ? this._userService.getUserDetail().pipe(ignoreElements(), startWith(null)) : of(null),
			this._userService.launchSocketUserDetail().pipe(ignoreElements(), startWith(null)),
		]).pipe(
			map(
				([store, loading]: [UserDetailInterface | null, boolean, null, null]): [
					UserDetailInterface | null,
					boolean,
				] => [store, loading],
			),
			tap((): void => {
				this._processingUserService.processUser()
			}),
		)
	}

	public launchPositions$(): Observable<[PositionsInterface[], boolean]> {
		const isNeedRequest: boolean =
			!this._positionsStocksHelperService.positions$.getValue().length &&
			!this._positionsStocksHelperService.isLoadingPositions$.getValue()

		return combineLatest([
			this._positionsStocksHelperService.positions$,
			this._positionsStocksHelperService.isLoadingPositions$,
			isNeedRequest
				? this._positionsStocksService.getAllPositions().pipe(ignoreElements(), startWith(null))
				: of(null),
			this._positionsStocksService.launchSocketPositions().pipe(ignoreElements(), startWith(null)),
		]).pipe(
			map(([store, loading]: [PositionsInterface[], boolean, null, null]): [PositionsInterface[], boolean] => [
				store,
				loading,
			]),
		)
	}

	public launchWatchList$(isNeedRequest: boolean = true): Observable<[WatchListResponseInterface | null, boolean]> {
		return combineLatest([
			this._watchListHelperService.watchList$,
			this._watchListHelperService.isLoadingWatchList$,
			isNeedRequest ? this._watchListService.getWatchList().pipe(ignoreElements(), startWith(null)) : of(null),
		]).pipe(
			map(
				([store, loading]: [WatchListResponseInterface | null, boolean, null]): [
					WatchListResponseInterface | null,
					boolean,
				] => [store, loading],
			),
		)
	}

	public launchOrders$(
		state: OrderStateEnum,
	): Observable<[OrdersResponseInterface | null | undefined, boolean, OrderParamsInterface | undefined, boolean]> {
		const orderResponse$: BehaviorSubject<OrdersResponseInterface | null> | undefined =
			this._ordersStocksHelperService.ordersResponseMap.get(state)
		const isLoadingOrders$: BehaviorSubject<boolean> | undefined =
			this._ordersStocksHelperService.ordersLoadingMap.get(state)
		const orderParams$: BehaviorSubject<OrderParamsInterface> | undefined =
			this._ordersStocksHelperService.ordersParamsMap.get(state)
		const isNeedUpdateOrders$: BehaviorSubject<boolean> | undefined =
			this._ordersStocksHelperService.isNeedUpdateOrderResponseMap.get(state)
		if (!orderResponse$ || !isLoadingOrders$ || !orderParams$ || !isNeedUpdateOrders$)
			return of([null, false, undefined, false])

		return combineLatest([
			orderResponse$,
			isLoadingOrders$,
			orderParams$,
			isNeedUpdateOrders$,
			this._ordersStocksService.getAllOrders(state).pipe(ignoreElements(), startWith(null)),
			state === this.orderStateEnum.OPEN
				? this._ordersStocksService.launchSocketOrders().pipe(ignoreElements(), startWith(null))
				: of(null),
			state === this.orderStateEnum.OPEN
				? this._ordersStocksService.launchSocketTrailOrders().pipe(ignoreElements(), startWith(null))
				: of(null),
		]).pipe(
			map(
				([store, loading, params, isNeedUpdateOrders]: [
					OrdersResponseInterface | null | undefined,
					boolean,
					OrderParamsInterface | undefined,
					boolean,
					null,
					OrderInterface | null,
					null,
				]): [
					OrdersResponseInterface | null | undefined,
					boolean,
					OrderParamsInterface | undefined,
					boolean,
				] => [store, loading, params, isNeedUpdateOrders],
			),
		)
	}

	public launchOptionsOrders$(
		state: OptionOrderStateEnum,
	): Observable<
		[
			OrdersResponseInterface<OptionOrderInterface> | null,
			boolean,
			OrderParamsInterface<OptionOrderStateEnum> | undefined,
			boolean,
		]
	> {
		const orderResponse$: BehaviorSubject<OrdersResponseInterface<OptionOrderInterface> | null> | undefined =
			this._optionsOrderHelperService.optionsOrdersResponseMap.get(state)
		const isLoading$: BehaviorSubject<boolean> | undefined =
			this._optionsOrderHelperService.optionsOrdersLoadingMap.get(state)
		const orderParams$: BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>> | undefined =
			this._optionsOrderHelperService.optionsOrdersParamsMap.get(state)
		const isNeedUpdateOrders$: BehaviorSubject<boolean> | undefined =
			this._optionsOrderHelperService.isNeedUpdateOptionsOrderResponseMap.get(state)
		if (!orderResponse$ || !isLoading$ || !orderParams$ || !isNeedUpdateOrders$)
			return of([null, false, undefined, false])

		return combineLatest([
			orderResponse$,
			isLoading$,
			orderParams$,
			isNeedUpdateOrders$,
			this._optionsOrderService.getOptionsOrders(state).pipe(ignoreElements(), startWith(null)),
			state === this.optionOrderStateEnum.OPEN
				? this._optionsOrderService
						.launchSocketOpenedOrdersOptions(this.optionOrderStateEnum.OPEN)
						.pipe(ignoreElements(), startWith(null))
				: of(null),
		]).pipe(
			map(
				([store, loading, params, isNeedUpdate]: [
					OrdersResponseInterface<OptionOrderInterface> | null,
					boolean,
					OrderParamsInterface<OptionOrderStateEnum> | undefined,
					boolean,
					null,
					null,
				]): [
					OrdersResponseInterface<OptionOrderInterface> | null,
					boolean,
					OrderParamsInterface<OptionOrderStateEnum> | undefined,
					boolean,
				] => [store, loading, params, isNeedUpdate],
			),
		)
	}

	public launchPositionsOptions$(): Observable<[PositionOptionInterface[], boolean]> {
		return combineLatest([
			this._positionOptionsHelperService.positionsOptions$,
			this._positionOptionsHelperService.isLoadingPositionsOptions$,
			this._positionsOptionsService.launchSocketPositionsOptions().pipe(ignoreElements(), startWith(null)),
		]).pipe(
			map(
				([store, loading]: [PositionOptionInterface[], boolean, null]): [
					PositionOptionInterface[],
					boolean,
				] => [store, loading],
			),
		)
	}

	public launchPostTradingEvents$(
		type: PostTradingEventTypeEnum,
	): Observable<[PostTradingEventInterface[], boolean]> {
		const postTradingEvents$: BehaviorSubject<PostTradingEventInterface[]> | undefined =
			this._postTradingHelperService.getPostTradingEventsByType$(type)
		const postTradingLoading$: BehaviorSubject<boolean> | undefined =
			this._postTradingHelperService.getPostTradingEventsLoadingByType$(type)
		if (!postTradingEvents$ || !postTradingLoading$) return of([[], false])

		return combineLatest([postTradingEvents$, postTradingLoading$]).pipe(
			map(([store, loading]: [PostTradingEventInterface[], boolean]): [PostTradingEventInterface[], boolean] => [
				store,
				loading,
			]),
		)
	}
}
